import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';
import buffer from '@turf/buffer';
import { feature, featureCollection } from '@turf/helpers';
import union from '@turf/union';

/**
 * Radiuses the parcels are grown by to bridge the gap between two boundaries meant to be shared, tried in order until
 * one produces a single polygon. Both sides of the gap grow, so a tolerance closes gaps up to twice its value: 80 cm,
 * then 150 cm. A wider gap is a real one and the merge is refused.
 */
const CONTIGUITY_TOLERANCES_IN_METERS = [0.4, 0.75];

function unionFeatures(features: Feature<Polygon | MultiPolygon>[]): Polygon | null {
  try {
    const merged = union(featureCollection(features));

    return merged?.geometry.type === 'Polygon' ? merged.geometry : null;
  } catch {
    // the clipping library gives up on some geometries with nearly coincident edges
    return null;
  }
}

/**
 * Distance under which two vertices of the same ring are the same point, around a millimeter. Layers describe a shared
 * boundary twice with a micrometric offset, which the clipping library keeps as a zero-width slit: the ring walks the
 * boundary and comes straight back along it. Comparing exactly would miss those vertices.
 */
const DUPLICATE_VERTEX_TOLERANCE_IN_DEGREES = 1e-8;

function hasRepeatedVertex(ring: Position[]): boolean {
  // the last position closes the ring by repeating the first one
  const vertices = ring.slice(0, -1);

  return vertices.some(([longitude, latitude], index) =>
    vertices
      .slice(index + 1)
      .some(
        ([otherLongitude, otherLatitude]) =>
          Math.abs(longitude! - otherLongitude!) < DUPLICATE_VERTEX_TOLERANCE_IN_DEGREES &&
          Math.abs(latitude! - otherLatitude!) < DUPLICATE_VERTEX_TOLERANCE_IN_DEGREES,
      ),
  );
}

// parcels merged along an imprecise edge either leave the sliver as a hole or pinch the outline where they touch
function hasSliver(polygon: Polygon): boolean {
  const [exteriorRing] = polygon.coordinates;

  return polygon.coordinates.length > 1 || (!!exteriorRing && hasRepeatedVertex(exteriorRing));
}

function bufferBy(f: Feature<Polygon | MultiPolygon>, toleranceInMeters: number) {
  try {
    return buffer(f, toleranceInMeters, { units: 'meters' });
  } catch {
    return undefined;
  }
}

// grow the parcels to make them overlap, merge them, then shrink the result back
function unionWithTolerance(features: Feature<Polygon | MultiPolygon>[], toleranceInMeters: number): Polygon | null {
  const grownFeatures = features.flatMap((f) => {
    const grown = bufferBy(f, toleranceInMeters);
    return grown ? [grown] : [];
  });
  if (grownFeatures.length !== features.length) return null;

  const grownMerged = unionFeatures(grownFeatures);
  if (!grownMerged) return null;

  const shrunk = bufferBy(feature(grownMerged), -toleranceInMeters);

  return shrunk?.geometry.type === 'Polygon' ? shrunk.geometry : null;
}

/**
 * Merges parcel geometries into a single polygon. Returns null when the geometries are not contiguous, since turf then
 * produces a MultiPolygon, which the parcel form does not support.
 *
 * Parcels coming from a layer often miss their shared edge by a few centimeters. Depending on whether they still touch
 * somewhere, the union either fails or keeps the gap as a sliver. Both are retried with a growing tolerance, which
 * rounds the corners of the result by up to that tolerance. Genuine holes are wider than the tolerance, so they survive
 * it.
 */
export function unionParcelGeometries(geometries: (Polygon | MultiPolygon)[]): Polygon | null {
  if (!geometries.length) return null;

  const features = geometries.map((geometry) => feature(geometry));

  const merged = unionFeatures(features);
  if (merged && !hasSliver(merged)) return merged;

  for (const tolerance of CONTIGUITY_TOLERANCES_IN_METERS) {
    const mergedWithTolerance = unionWithTolerance(features, tolerance);
    if (mergedWithTolerance) return mergedWithTolerance;
  }

  return merged;
}
